import { Component, Q } from '@fullstory/tomato';
import { RendererView } from '@fullstory/tomato-views/views';
import { RawEvent } from '@fullstory/recording-api';
import {
  HighlightElementIdentifiers,
  ObjectCollection,
  ObjectInfo,
  GroupedObjectInfo,
  ObjectPathPattern,
  ObjectQuery,
  ObjectSelectorPattern,
  PlaybackContext,
  PlayResult,
  Renderer,
  RendererCursor,
  RendererDimensions,
  RendererEvent,
  RendererHost,
  RendererKeyboardEvent,
  RendererModeOptions,
  RendererPointerEvent,
  SeekResult,
  ViewportOverrides,
  GroupByPropertyDisplay,
  FilteredElementRecord,
} from '@fullstory/renderer-shared';
import { ResourcePreloader } from '@fullstory/resource-preloader';
import { OverlayMode, Overlays } from './overlays/overlay';
import { Flags, PageDesc } from '@fullstory/client-api';
import { Dispatcher } from '@fullstory/channel/proxy';
import { maxHighlightedElements } from './page-insights/activitymapsconst';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { Point } from '@fullstory/util/geometry';
import { assertExhaustive } from '@fullstory/typeutils';
import {
  addPropertyValuesForRule,
  apiPropertyValuesForObject,
  computePropertyValuesConfig,
  ComputedPropertyValue,
  computeFocusPaths,
  satisfiesFocus,
  satisfiesConstraints,
  getComputedNormalizedPropertyValue,
} from './page-insights/properties';
import { FsPropertiesSchemaAttributes } from '@fullstory/named-elements-ui';
import { uniq } from 'lodash';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';

const INSIGHTS_ANIMATION_DURATION_MS = 400;
const ZOOM_Y_PADDING_PX = 55;

type DisplayMode = 'fullscreen' | 'explore';

// Abstract base class for Android and Web RendererImpls running inside the off-domain IFrame.
export abstract class AbstractRendererImpl extends Component<RendererView> implements Renderer {
  protected readonly _preloader: ResourcePreloader;

  #handleKeyboardEvent: (e: KeyboardEvent) => void;
  #handlePointerEvent: (e: PointerEvent) => void;
  #handleResizeEvent: () => void;
  private _frameWidth: number;
  private _frameHeight: number;
  private _visualScale: number;
  private _zoomScale: number;
  private _overlays: Overlays;

  private _activeOverlay: OverlayMode;
  private _inspector: OverlayMode;
  private _activityMaps: OverlayMode;
  private _scrollMaps: OverlayMode;
  private _mode: DisplayMode;
  private _viewportOverrides: ViewportOverrides | undefined;
  #resizeObserver: ResizeObserver;
  /** A frame height to use instead of the calculated frame height. */
  private _fullHeightFrameOverride: number | null;

  private _transitioning = false; // Whether or not we are in the process of loading/unloading page insights.

  private _frameScale$: BehaviorSubject<number>;
  private _zoomSubscription: Subscription | null;

  protected readonly _flags: Flags;

  #observedObjects: ObjectQuery = { patterns: [] };

  protected constructor(
    protected readonly _host: RendererHost,
    protected readonly _dispatcher: Dispatcher,
    protected readonly _ctx: PlaybackContext,
  ) {
    super(new RendererView());
    this._preloader = new ResourcePreloader();
    this._overlays = this.createOverlays();
    this.#handleResizeEvent = () => {
      if (this._frameWidth && this._frameHeight && this._visualScale) {
        this.scalePlaybackFor(this._frameWidth, this._frameHeight, this._visualScale);
      }
    };

    if (this._ctx.flags.features.AppPlaybackRendererAccessibleTouchColorChange) {
      this.view().addClass('ff-accessible-click-overlays');
    }

    this._frameScale$ = new BehaviorSubject<number>(0);

    this.#handleKeyboardEvent = event => {
      const keyboardEvent = toRendererKeyboardEvent(event);
      if (keyboardEvent) {
        this.onEvent(keyboardEvent);
      }
    };
    this.#handlePointerEvent = event => {
      const pointerEvent = toRendererPointerEvent(event);
      if (pointerEvent) {
        this.onEvent(pointerEvent);
      }
    };

    // When the renderer's dimensions change, we need to update the styles of
    // various elements and then read back the new dimensions. We handle the
    // updates synchronously in #handleResizeEvent() and the readback
    // asynchronously in the ResizeObserver callback. This is better for
    // performance since it allows multiple updates to be coalesced into a
    // single layout flush when things are changing quickly.
    this.#resizeObserver = new ResizeObserver(this.onResize);
    this.#resizeObserver.observe(this.view().e);
    this.#resizeObserver.observe(this.view().scaled.e);
    // Let's also watch the scaled container transform transitions that don't get picked up by the resize observer.
    this.view().scaled.e.addEventListener('transitionend', this.onTransitionEnd);

    window.addEventListener('keydown', this.#handleKeyboardEvent, true);
    window.addEventListener('keyup', this.#handleKeyboardEvent, true);

    const docElem = document.documentElement;
    docElem.addEventListener('pointerdown', this.#handlePointerEvent, true);
    docElem.addEventListener('pointerup', this.#handlePointerEvent, true);
    docElem.addEventListener('pointermove', this.#handlePointerEvent, {
      capture: true,
      passive: true,
    });
    docElem.addEventListener('pointerover', this.#handlePointerEvent, {
      capture: true,
      passive: true,
    });
    docElem.addEventListener('pointerout', this.#handlePointerEvent, {
      capture: true,
      passive: true,
    });

    window.addEventListener('resize', this.#handleResizeEvent, false);

    this.view().appendTo(document.body);

    this._flags = new Flags(this._ctx.flags.client);
    this.view().addClass('is-map-mode');
    // add key value colors
    for (let i = 0; i < maxHighlightedElements; i++) {
      this.view().mapKey.mapKeys.append(
        Q.createOne('div')
          .addClass(`map-key-color-${i + 1}`)
          .addClass('map-key-item'),
      );
    }
  }

  hasWebViewAt(_offsetMs: number, _opts: { onlyIncludeRecorded?: boolean } = {}): Promise<boolean> {
    return Promise.resolve(false);
  }

  protected abstract createOverlays(): Overlays;

  playbackContainer(): Q.OfOne {
    return this.view().frame;
  }

  overlays(): Overlays {
    return this._overlays;
  }

  public currentFrameScale(): number {
    return this._frameScale$.getValue();
  }

  public currentZoomScale(): number {
    return this._zoomScale;
  }

  /**
   * @return the current scale being applied to the playback iframe due to the
   * visual viewport.
   */
  public currentVisualScale(): number {
    return this._visualScale;
  }

  protected scalePlaybackFor(
    frameWidthArg: number,
    frameHeightArg: number,
    visualScale: number,
  ): void {
    // frameWidth and frameHeight values of exactly 1 can trigger a performance bug in
    // Chrome. See CH39775 and https://crbug.com/892188.
    let frameWidth = frameWidthArg;
    let frameHeight = frameHeightArg;

    if (frameWidth == 1) {
      frameWidth = 0;
    }
    if (frameHeight == 1) {
      frameHeight = 0;
    }

    // If we haven't initialized the frame height or width yet, just bail
    if (!frameWidth || !frameHeight) {
      return;
    }

    // Cache this for future resizes
    this._frameWidth = frameWidth;
    this._frameHeight = frameHeight;
    this._visualScale = visualScale;

    const view = this.view();

    // Calculate the scale/offset to center the outer frame.
    const cWidth = view.offsetWidth();
    const cHeight = view.offsetHeight();

    // TODO(jaime): I think we have some kind of rounding error or minor math issue.
    // IFrame embeds are like 2px off, causing un-necessary scaling of the playback frame.
    const xScale = cWidth / frameWidth,
      yScale = cHeight / frameHeight;
    let frameScale = xScale < yScale ? xScale : yScale;
    let frameOffsetX = (cWidth - frameScale * frameWidth) / 2;
    let frameOffsetY = (cHeight - frameScale * frameHeight) / 2;

    const BASICALLY_ZERO_THRESHOLD = 0.0001;

    // Clamp to zero if basically zero (otherwise bad things happen because CSS
    // doesn't accept arbitrary JS floats because science is hard).
    if (Math.abs(frameOffsetX) < BASICALLY_ZERO_THRESHOLD) {
      frameOffsetX = 0;
    }
    if (Math.abs(frameOffsetY) < BASICALLY_ZERO_THRESHOLD) {
      frameOffsetY = 0;
    }

    if (this.isFullPageMode()) {
      view.scaled.setCss('width', '100%');
      view.scaled.setCss('height', '100%');
      view.scaled.setCss('left', 0);
      view.scaled.setCss('top', 0);
      frameScale = 1;
    } else {
      if (this._viewportOverrides?.useFullPlaybackHeight && this._fullHeightFrameOverride) {
        view.scaled.setCss('height', this._fullHeightFrameOverride / frameScale);
      } else {
        view.scaled.setCss('height', frameHeight);
      }
      view.scaled.setCss('width', frameWidth);
      view.scaled.setCss('left', frameOffsetX);
      view.scaled.setCss('top', frameOffsetY);
    }

    if (this.isHeatmapsExploreMode()) {
      // Close the margin above the frame, so that the visualization is more likely to be above the fold
      view.scaled.setCss('top', 0);
    }

    if (frameScale == 0) {
      // The renderer is hidden, so we couldn't calculate frameScale correctly.
      return;
    }
    if (frameScale === this._frameScale$.getValue()) {
      // No need to trigger any further changes. This helps prevent visual thrashing.
      return;
    }

    view.scaled.setCssTransform(`scale(${frameScale}, ${frameScale})`);
    this._frameScale$.next(frameScale);
  }

  //////////////////////////////////////////////
  // Abstract methods that must be implemented
  //////////////////////////////////////////////
  abstract createInspector(): OverlayMode;

  abstract createActivityMaps(): OverlayMode;

  //////////////////////////////////////////////
  // Methods that should be overridden.
  //////////////////////////////////////////////
  createScrollMaps(): OverlayMode {
    console.debug('not implemented');
    return {
      start: () => {},
      stop: () => {},
    };
  }

  highlightElements(_highlightElementIdentifiers?: HighlightElementIdentifiers) {}

  /**
   * Invalidate any caches which may depend on the dimensions or scaling of the
   * renderer itself.
   */
  abstract invalidateRendererGeometryCaches(): void;

  ///////////////////////
  // Implement Renderer.
  ///////////////////////
  abstract openPage(page: PageDesc): void;

  abstract playPage(raw: any): Promise<PlayResult>;

  abstract playToTime(pageOffset: number, requestId?: number | string): Promise<SeekResult>;

  abstract onPause(): Promise<void>;

  abstract appendTrailingEvents(trailingEvents: RawEvent[]): void;

  abstract waitForImagesToLoad(maxWaitTimeMs: number): Promise<void>;

  // Hooks for subclasses to adjust rendering / scaling for page insights.
  protected abstract beforeEnteringPageInsights(): void;

  protected abstract afterLeavingPageInsights(): void;

  enableFullPageMode() {
    this._mode = 'fullscreen';
    const view = this.view();
    view.scaled.setCss('width', '100%');
    view.scaled.setCss('height', '100%');
    view.viewport.setCss('width', '100%');
    view.viewport.setCss('height', '100%');
    view.frame.setCss('width', '100%');
    view.frame.setCss('height', '100%');
  }

  enableHeatmapsExploreMode() {
    this._mode = 'explore';
  }

  enterPageInsights() {
    if (this._transitioning || this._activeOverlay) {
      return;
    }

    this.beforeEnteringPageInsights();

    this._zoomSubscription = this._frameScale$.subscribe(_ => {
      this._zoomOut();
    });
  }

  private _zoomOut() {
    this._transitioning = true;
    const view = this.view();
    view.addClass('pageInsights');
    setTimeout(() => {
      const container = this.playbackContainer().elem();
      const width = container.offsetWidth;
      const height = container.offsetHeight;

      let scale = 1;
      let left = 0;
      let top = 0;

      if (!this.isFullPageMode()) {
        scale = (height - ZOOM_Y_PADDING_PX / this.currentFrameScale()) / height;
        left = this.isFullPageMode() ? 0 : (width - scale * width) / 2;
        top = this.isFullPageMode() ? 0 : (height - scale * height) / 2;
      }

      this._zoomScale = scale;
      if (!this.isFullPageMode()) {
        scale = scale * this.currentFrameScale();
      }

      view.scaled.setCss('transform', `scale(${scale}, ${scale}) translate(${left}px, ${top}px) `);
      view.scaled.setCss(
        '-webkit-transform',
        `scale(${scale}, ${scale}) translateX(${left}px, ${top}px) `,
      );

      // Check for scale !== -Infinity to prevent setting the height to 0 if we can't read the container height
      if (
        this._viewportOverrides?.useFullPlaybackHeight &&
        this._fullHeightFrameOverride &&
        scale !== -Infinity
      ) {
        view.scaled.setCss('height', (this._fullHeightFrameOverride - ZOOM_Y_PADDING_PX) / scale);
      }

      this._transitioning = false;
      this.overlays().resize();
    }, 10);
  }

  leavePageInsights() {
    this._zoomSubscription?.unsubscribe();
    if (this._transitioning) {
      return;
    }
    this._transitioning = true;
    const activeOverlay = this._activeOverlay;
    this._activeOverlay = null;

    const scale = this.currentFrameScale();
    const view = this.view();
    view.scaled.setCss('transform', `scale(${scale}, ${scale})`);
    view.scaled.setCss('-webkit-transform', `scale(${scale}, ${scale})`);
    setTimeout(() => {
      view.removeClass('pageInsights');
      if (activeOverlay) {
        activeOverlay.stop();
      }
      this._transitioning = false;

      this.afterLeavingPageInsights();
    }, INSIGHTS_ANIMATION_DURATION_MS);
  }

  showInspector() {
    if (!this._inspector) {
      this._inspector = this.createInspector();
    }
    this._switchOverlayMode(this._inspector);
    this.enterPageInsights();
  }

  showActivityMaps() {
    if (!this._activityMaps) {
      this._activityMaps = this.createActivityMaps();
    }
    this._switchOverlayMode(this._activityMaps);
    this.enterPageInsights();
  }

  showScrollMaps() {
    if (!this._scrollMaps) {
      this._scrollMaps = this.createScrollMaps();
    }
    this._switchOverlayMode(this._scrollMaps);
    this.enterPageInsights();
  }

  private _switchOverlayMode(mode: OverlayMode) {
    if (this._activeOverlay != mode) {
      mode.start();
    }
    if (this._activeOverlay != mode) {
      if (this._activeOverlay) {
        this._activeOverlay.stop();
      }
      this._activeOverlay = mode;
    }
  }

  hideGlassPane() {
    this._overlays.hideGlassPane();
  }

  showGlassPane() {
    this._overlays.showGlassPane();
  }

  async getDimensions(): Promise<RendererDimensions> {
    return this.#getDimensions();
  }

  #getDimensions(): RendererDimensions {
    return {
      contentArea: this.view().scaled.e.getBoundingClientRect(),
      rendererArea: this.view().e.getBoundingClientRect(),
    };
  }

  teardown() {
    this.leavePageInsights();
    if (this.#handleKeyboardEvent) {
      window.removeEventListener('keydown', this.#handleKeyboardEvent, true);
      window.removeEventListener('keyup', this.#handleKeyboardEvent, true);
      this.#handleKeyboardEvent = null;
    }
    if (this.#handlePointerEvent) {
      window.removeEventListener('pointerdown', this.#handlePointerEvent, { capture: true });
      window.removeEventListener('pointerup', this.#handlePointerEvent, { capture: true });
      window.removeEventListener('pointermove', this.#handlePointerEvent, { capture: true });
      window.removeEventListener('pointerover', this.#handlePointerEvent, { capture: true });
      window.removeEventListener('pointerout', this.#handlePointerEvent, { capture: true });
      this.#handlePointerEvent = null;
    }
    if (this.#handleResizeEvent) {
      window.removeEventListener('resize', this.#handleResizeEvent, false);
      this.#handleResizeEvent = null;
    }
    this.#observedObjects = { patterns: [] };
    this._frameScale$.complete();
    this.#resizeObserver.disconnect();
    this.view().scaled.e.removeEventListener('transitionend', this.onTransitionEnd);
  }

  /**
   * If the player is in "full page mode" which means no scaling should be done and the entire page should have the
   * height and width set to 100% instead of trying to mimic the viewport of the original session. This is mainly used
   * for image export.
   */
  isFullPageMode() {
    return this._mode === 'fullscreen';
  }

  isHeatmapsExploreMode() {
    return this._mode === 'explore';
  }

  isInternalScrollingPage(_selectors: string[]): boolean {
    return false;
  }

  scrollToElement(
    _elementSelector: string,
    _propertyValue: string | undefined,
    _focusSelectors: string[],
    _groupByProperty: GroupByPropertyDisplay | undefined,
  ) {}

  computeAvailableProperties(
    focusSelectors: string[],
    allGroupByProperty: Map<string, GroupByPropertyDisplay>,
  ): string[] {
    const availableProperties: string[] = [];
    const focusPaths = computeFocusPaths(focusSelectors, this);

    // first, run the rules one property at a time to understand which have computed values
    for (const [propertyName, groupByProperty] of allGroupByProperty.entries()) {
      const computedPropertyValues = new Map<string, ComputedPropertyValue>();
      for (const rule of groupByProperty.propertyRules) {
        addPropertyValuesForRule(rule, focusPaths, computedPropertyValues, this);
        if (computedPropertyValues.size) {
          availableProperties.push(propertyName);
          break;
        }
      }
    }

    // then, for any remaining properties, let's check for api declarations
    const propertiesToCheck = new Set(allGroupByProperty.keys());
    availableProperties.forEach(prop => propertiesToCheck.delete(prop));
    const propertiesSchemaCollection = this.queryObjects({
      patterns: FsPropertiesSchemaAttributes.map(key => ({
        type: 'selector',
        selector: `[${key}]`,
      })),
    });
    for (const [path, object] of Object.entries(propertiesSchemaCollection)) {
      if (!satisfiesFocus(focusPaths, path, true, this)) {
        continue;
      }
      const propertyValues = apiPropertyValuesForObject(propertiesToCheck, object);
      for (const value of propertyValues) {
        if (propertiesToCheck.delete(value.propertyName)) {
          availableProperties.push(value.propertyName);
        }
      }
    }

    return availableProperties;
  }

  computePropertyValues(
    focusSelectors: string[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ): string[] {
    const focusPaths = computeFocusPaths(focusSelectors, this);
    const propertyValuesConfig = computePropertyValuesConfig(focusPaths, groupByProperty, this);
    if (!propertyValuesConfig) {
      return [];
    }
    const propertyValues = [...propertyValuesConfig.computedPropertyValues.values()]
      .map(v => v.normalizedValue)
      .filter((v): v is string => v !== undefined);
    return uniq(propertyValues);
  }

  zeroCurrentScrollPosition() {}

  /**
   * Gets the full HTML content of the playback frame as a string.
   * Default implementation returns null as access to the frame's HTML depends on the specific renderer implementation.
   *
   * @returns The full HTML content of the playback frame as a string, or null if not available
   */
  getPlaybackFrameHTML(): string | null {
    return null;
  }

  /**
   * Sets the text corpus for the renderer. This is used to replace text in the playback frame
   * with the given text when matched.
   *
   * Note: this is currently an alpha feature and essentially only works as intended for:
   * - web renderer
   * - when the text corpus is set before the page is opened
   *
   * A deeper integration is needed for the future if we want to support
   *
   * @param textCorpus A mapping of text to replace with.
   */
  setTextCorpus(textCorpus: Record<string, string>) {
    this._ctx.page.textCorpus = textCorpus;
  }

  filterRelatedNamedElements(namedElements: NamedElementRecord[]): NamedElementRecord[] {
    return this.filterRelatedElements([], namedElements, undefined).map(
      namedElement => namedElement.namedElementRecord,
    );
  }

  filterRelatedElements(
    focusSelectors: string[],
    namedElements: NamedElementRecord[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ): FilteredElementRecord[] {
    const handled = new Map<string, FilteredElementRecord>();
    const relatedNamedElements: FilteredElementRecord[] = [];

    const platform = this.getNativePlatform();
    const focusPaths = computeFocusPaths(focusSelectors, this);
    const propertyValuesConfig = computePropertyValuesConfig(focusPaths, groupByProperty, this);

    for (const namedElement of namedElements) {
      if (!namedElement?.namedElement?.platformSelectors) {
        continue;
      }

      for (const selector of namedElement.namedElement.platformSelectors) {
        if (selector.platforms.length && !selector.platforms.includes(platform)) {
          continue;
        }
        const matches = this.querySelector(selector.selector);
        for (const match of matches) {
          const propertyValue = propertyValuesConfig
            ? getComputedNormalizedPropertyValue(
                match.path,
                propertyValuesConfig.computedPropertyValues,
                this,
              )
            : undefined;
          const hasPropertyValueIfRequired = propertyValuesConfig
            ? propertyValue !== undefined
            : true;
          const handledKey =
            propertyValue !== undefined
              ? `${namedElement.namedElement?.id ?? ''}!!${propertyValue}`
              : `${namedElement.namedElement?.id ?? ''}`;
          // intentionally not using property values config for satisfies constraints here
          // as we aren't checking property values, rather we are producing them
          const existing = handled.get(handledKey);
          if (!existing?.satisfiesConstraints) {
            // either no existing, or doesn't satisfy constraints so we need to check again
            const relatedNamedElement = existing ?? {
              namedElementRecord: namedElement,
              propertyValue,
              satisfiesConstraints: false,
            };
            if (relatedNamedElement !== existing) {
              relatedNamedElements.push(relatedNamedElement);
              handled.set(handledKey, relatedNamedElement);
            }
            relatedNamedElement.satisfiesConstraints =
              hasPropertyValueIfRequired &&
              satisfiesConstraints(undefined, match.path, focusPaths, undefined, this);
          }
        }
      }
    }

    return relatedNamedElements;
  }

  setViewportOverrides(viewportOverrides: ViewportOverrides) {
    this._viewportOverrides = viewportOverrides;
    this.#handleResizeEvent();
  }

  onTransitionEnd = (event: TransitionEvent) => {
    if (event.propertyName === 'transform') {
      this.onResize();
    }
  };

  onResize = () => {
    this.overlays().resize();
    const dimensions = this.#getDimensions();
    this._fullHeightFrameOverride = dimensions.rendererArea.height;
    this.invalidateRendererGeometryCaches();
    this.onEvent({ type: 'resize', dimensions });
    this.onObjectsMayHaveChanged();
  };

  onObjectsMayHaveChanged = () => {
    if (this.#observedObjects.patterns.length === 0) {
      return;
    }

    const objects = this.queryObjects(this.#observedObjects);
    this.onEvent({ type: 'observation', objects });
  };

  observeObjects(query: ObjectQuery) {
    this.#observedObjects = query;
    return this.onObjectsMayHaveChanged();
  }

  queryObjects(query: ObjectQuery): ObjectCollection {
    const collection: ObjectCollection = {};
    const collectResults = (results: ObjectInfo[], groups: string[] = []) => {
      for (const newResult of results) {
        const result: GroupedObjectInfo = collection[newResult.path] ?? {
          ...newResult,
          groups: [...groups],
        };
        // both groups and optional underlying fields driven by query options should be merged
        if (result !== newResult) {
          result.groups.push(...groups);
          if (newResult.innerText !== undefined) {
            result.innerText = newResult.innerText;
          }
        }
        collection[newResult.path] = result;
      }
    };

    // Gather results for each pattern in the query.
    for (const pattern of query.patterns) {
      switch (pattern.type) {
        case 'path': {
          const objectInfo = this.queryPathPattern(pattern);
          if (objectInfo) {
            collectResults([objectInfo], pattern.groups);
          }
          break;
        }

        case 'selector': {
          if (pattern.selector !== '') {
            collectResults(this.querySelectorPattern(pattern), pattern.groups);
          }
          break;
        }

        default:
          assertExhaustive(pattern);
      }
    }

    // Deduplicate groups.
    for (const result of Object.values(collection)) {
      result.groups = Array.from(new Set(result.groups));
    }

    // And if requested, compute descendant data as a last pass across all collected objects.
    if (query.computeDescendants) {
      this.computeDescendants(collection);
    }

    return collection;
  }

  abstract queryPathPattern(query: ObjectPathPattern): ObjectInfo | undefined;
  abstract querySelectorPattern(query: ObjectSelectorPattern): ObjectInfo[];

  queryPoint(point: Point): ObjectInfo[] {
    return this.queryObjectsAtPoint(point);
  }

  abstract queryObjectsAtPoint(point: Point): ObjectInfo[];

  querySelector(selector: string): ObjectInfo[] {
    if (selector === '') return [];
    return this.querySelectorPattern({ type: 'selector', selector });
  }

  isDescendant(testPath: string, basePath: string): boolean {
    return false;
  }

  computeDescendants(collection: ObjectCollection) {}

  abstract getNativePlatform(): NativePlatform;

  async setMode(options: RendererModeOptions): Promise<void> {
    switch (options.mode) {
      case 'interactive': {
        this.hideGlassPane();
        this.overlays().setPointerAndTouchIndicatorsVisible(false);
        await this.setCursorOverride(options.cursor ?? 'default');
        await this.setInteractive(true);
        return;
      }

      case 'playback': {
        this.showGlassPane();
        this.overlays().setPointerAndTouchIndicatorsVisible(true);
        await this.setCursorOverride('captured');
        await this.setInteractive(false);
        return;
      }

      default: {
        assertExhaustive(options);
      }
    }
  }

  abstract setCursorOverride(cursor: RendererCursor): Promise<void>;
  abstract setInteractive(interactive: boolean): Promise<void>;

  onEvent = async (event: RendererEvent) => {
    switch (event.type) {
      case 'pointermove':
      case 'pointerdown':
      case 'pointerup': {
        const targets = await this.queryPoint({ x: event.clientX, y: event.clientY });
        event.targets.push(...targets);
        break;
      }
    }
    this._host.onEvent(event);
  };
}

function toRendererKeyboardEvent(e: KeyboardEvent): RendererKeyboardEvent | undefined {
  switch (e.type) {
    case 'keydown':
    case 'keyup':
      return {
        type: e.type,
        altKey: e.altKey,
        code: e.code,
        ctrlKey: e.ctrlKey,
        key: e.key,
        keyCode: e.keyCode,
        location: e.location,
        metaKey: e.metaKey,
        repeat: e.repeat,
        shiftKey: e.shiftKey,
      };

    default:
      return undefined;
  }
}

function toRendererPointerEvent(e: PointerEvent): RendererPointerEvent | undefined {
  switch (e.type) {
    case 'pointerdown':
    case 'pointerup':
    case 'pointermove':
    case 'pointerover':
    case 'pointerout':
      return {
        type: e.type,
        altKey: e.altKey,
        button: e.button,
        buttons: e.buttons,
        clientX: e.clientX,
        clientY: e.clientY,
        ctrlKey: e.ctrlKey,
        isPrimary: e.isPrimary,
        metaKey: e.metaKey,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
        screenX: e.screenX,
        screenY: e.screenY,
        shiftKey: e.shiftKey,
        targets: [],
      };

    default:
      return undefined;
  }
}
